/**
 * Trade Communications content: email/limbo list (with threading) + detail panel
 */
import { useMemo, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SearchLg, X } from "@untitledui/icons";
import { ActivityItemView } from "@/components/application/activity-feed/activity-item";
import { LimboMessageItem } from "@/components/application/email/limbo-message-item";
import { EmailDetailPanel } from "./email-detail-panel";
import { Input } from "@/components/base/input/input";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { getEmailMessageIdFromActivity, type TradeCommsFolder, type ActivityItem, type LimboMessage } from "./types";

export interface TradeCommsContentProps {
    folder: TradeCommsFolder;
    /** Email activities (from useActivities with activity_category: email) */
    emailActivities: ActivityItem[];
    /** Limbo messages (from useLimboMessages) */
    limboMessages: LimboMessage[];
    onMarkActivityAsRead?: (activityId: string) => void;
    /** Show as threads (group by container) for inbox; default true for inbox */
    threadByContainer?: boolean;
    className?: string;
    // Search and infinite scroll props
    searchInput?: string;
    onSearchChange?: (value: string) => void;
    onLoadMoreInbox?: () => void;
    onLoadMoreLimbo?: () => void;
    hasMoreInbox?: boolean;
    hasMoreLimbo?: boolean;
    isLoadingInbox?: boolean;
    isLoadingLimbo?: boolean;
    initialMessageId?: string | null;
    onDetailOpen?: () => void;
    onDetailClose?: () => void;
}

/** Group email activities by container; each group sorted DESC by timestamp */
function groupActivitiesByContainer(activities: ActivityItem[]): { containerId: string | null; items: ActivityItem[] }[] {
    const byContainer = new Map<string | null, ActivityItem[]>();
    for (const a of activities) {
        const key = a.metadata?.containerId ?? null;
        const arr = byContainer.get(key) ?? [];
        arr.push(a);
        byContainer.set(key, arr);
    }
    return Array.from(byContainer.entries()).map(([containerId, items]) => {
        items.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        return { containerId, items };
    });
}

/** Flatten grouped threads and sort groups by most recent item DESC */
function sortThreadsByRecent(groups: { containerId: string | null; items: ActivityItem[] }[]): ActivityItem[] {
    const withMax = groups.map((g) => ({ ...g, maxTs: g.items[0]?.timestamp.getTime() ?? 0 }));
    withMax.sort((a, b) => b.maxTs - a.maxTs);
    return withMax.flatMap((g) => g.items);
}

export function TradeCommsContent({
    folder,
    emailActivities,
    limboMessages,
    onMarkActivityAsRead,
    threadByContainer = true,
    className,
    searchInput = "",
    onSearchChange,
    onLoadMoreInbox,
    onLoadMoreLimbo,
    hasMoreInbox = false,
    hasMoreLimbo = false,
    isLoadingInbox = false,
    isLoadingLimbo = false,
    initialMessageId,
    onDetailOpen,
    onDetailClose,
}: TradeCommsContentProps) {
    const { t } = useTranslation(["pages:activity"]);
    const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
    const [selectedAttentionLabels, setSelectedAttentionLabels] = useState<string[]>([]);
    const listRef = useRef<HTMLDivElement>(null);

    const isInbox = folder.type === "inbox" || (folder.type === "account" && folder.sub === "all");
    const isLimbo = folder.type === "limbo" || (folder.type === "account" && folder.sub === "limbo");

    // Reset selected message when folder changes
    useEffect(() => {
        setSelectedMessageId(null);
        setSelectedAttentionLabels([]);
        onDetailClose?.();
    }, [folder, onDetailClose]);

    // Auto-select message from URL deep-link
    useEffect(() => {
        if (initialMessageId) {
            setSelectedMessageId(initialMessageId);
            setSelectedAttentionLabels([]);
            onDetailOpen?.();
        }
    }, [initialMessageId, onDetailOpen]);

    const openDetail = (messageId: string | null, attentionLabels?: string[]) => {
        setSelectedMessageId(messageId);
        setSelectedAttentionLabels(attentionLabels ?? []);
        if (messageId) onDetailOpen?.();
        else onDetailClose?.();
    };

    const filteredActivities = useMemo(() => {
        if (!isInbox) return [];
        let list = emailActivities;
        if (folder.type === "account") {
            list = list.filter((a) => a.metadata?.accountEmail === folder.email);
        }
        list = [...list].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        if (threadByContainer) {
            const groups = groupActivitiesByContainer(list);
            return sortThreadsByRecent(groups);
        }
        return list;
    }, [emailActivities, folder, isInbox, threadByContainer]);

    const filteredLimbo = useMemo(() => {
        if (!isLimbo) return [];
        if (folder.type === "account" && folder.sub === "limbo") {
            return limboMessages.filter((m) => m.account_id === folder.accountId);
        }
        return limboMessages;
    }, [limboMessages, folder, isLimbo]);

    const handleSelectActivity = (item: ActivityItem) => {
        if (onMarkActivityAsRead && !item.isRead) onMarkActivityAsRead(item.id);
        const msgId = getEmailMessageIdFromActivity(item);
        openDetail(msgId ?? null, item.metadata?.emailAttentionLabels);
    };

    const handleSelectLimbo = (message: LimboMessage) => {
        openDetail(message.message_id, []);
    };

    const showInboxList = isInbox && (filteredActivities.length > 0 || filteredLimbo.length === 0);
    const showLimboList = isLimbo && filteredLimbo.length > 0;
    const isDetailOpen = Boolean(selectedMessageId);
    
    // Infinite scroll handler
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        const scrolledToBottom = scrollHeight - scrollTop - clientHeight < 50; // 50px threshold
        
        if (scrolledToBottom) {
            if (isInbox && hasMoreInbox && !isLoadingInbox) {
                onLoadMoreInbox?.();
            } else if (isLimbo && hasMoreLimbo && !isLoadingLimbo) {
                onLoadMoreLimbo?.();
            }
        }
    };

    return (
        <div className={className ?? "flex flex-1 min-h-0 rounded-r-xl border border-secondary border-l-0 bg-primary overflow-hidden"}>
            <div className="flex flex-1 min-h-0 min-w-0">
                <div
                    className={`flex flex-col overflow-hidden bg-primary/30 ${
                        isDetailOpen
                            ? "basis-1/3 min-w-[280px] max-w-[500px] shrink-0 border-r border-secondary"
                            : "w-full"
                    }`}
                >
                    {/* Search field */}
                    {onSearchChange && (
                        <div className="p-3 border-b border-secondary">
                            <div className="relative">
                                <Input
                                    placeholder={t("tradeComms.searchEmails", { defaultValue: "Search emails..." })}
                                    value={searchInput}
                                    onChange={(value: string) => onSearchChange(value)}
                                    className="pl-9 pr-8"
                                />
                                <SearchLg className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-tertiary pointer-events-none" />
                                {searchInput && (
                                    <button
                                        type="button"
                                        onClick={() => onSearchChange("")}
                                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-tertiary hover:text-primary"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                    
                    {showInboxList && (
                        <div className="p-2 border-b border-secondary">
                            <h3 className="text-sm font-medium text-primary">
                                {folder.type === "account" ? folder.email : t("tradeComms.allInbox", { defaultValue: "All Inbox Email" })}
                            </h3>
                        </div>
                    )}
                    {showLimboList && (
                        <div className="p-2 border-b border-secondary">
                            <h3 className="text-sm font-medium text-primary">
                                {folder.type === "account" ? t("tradeComms.reviewNeeded", { defaultValue: "Review Needed" }) : t("tradeComms.allLimbo", { defaultValue: "All Limbo Email" })}
                            </h3>
                        </div>
                    )}
                    <div 
                        ref={listRef}
                        onScroll={handleScroll}
                        className="flex-1 overflow-y-auto p-2 space-y-1 max-h-screen"
                    >
                        {showInboxList &&
                            (() => {
                                const grouped = groupActivitiesByContainer(filteredActivities);
                                const sorted = sortThreadsByRecent(grouped);
                                return (
                                    <div className="space-y-0 divide-y divide-secondary">
                                        {sorted.map((item) => (
                                            <div
                                                key={item.id}
                                                className="transition-colors hover:bg-secondary/30 focus-within:bg-secondary/30"
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => handleSelectActivity(item)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" || e.key === " ") {
                                                        e.preventDefault();
                                                        handleSelectActivity(item);
                                                    }
                                                }}
                                            >
                                                <ActivityItemView
                                                    item={item}
                                                    onRead={onMarkActivityAsRead}
                                                    onOpenEmailModal={(id) => openDetail(id)}
                                                    hideOpenEmailAction
                                                    tradeCommsInboxEmailList
                                                />
                                            </div>
                                        ))}
                                    </div>
                                );
                            })()}
                        {showLimboList &&
                            (() => {
                                const sorted = [...filteredLimbo].sort((a, b) => new Date(b.received_at).getTime() - new Date(a.received_at).getTime());
                                return (
                                    <div className="space-y-0 divide-y divide-secondary">
                                        {sorted.map((msg) => (
                                            <div
                                                key={msg.message_id}
                                                className="transition-colors hover:bg-secondary/30 cursor-pointer"
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => handleSelectLimbo(msg)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" || e.key === " ") {
                                                        e.preventDefault();
                                                        handleSelectLimbo(msg);
                                                    }
                                                }}
                                            >
                                                <LimboMessageItem message={msg} />
                                            </div>
                                        ))}
                                    </div>
                                );
                            })()}
                        
                        {/* Loading indicator for limbo infinite scroll */}
                        {showLimboList && isLoadingLimbo && (
                            <div className="flex justify-center items-center py-4">
                                <LoadingIndicator type="line-spinner" size="sm" />
                            </div>
                        )}
                        
                        {isInbox && filteredActivities.length === 0 && !isLimbo && (
                            <div className="flex flex-col items-center justify-center py-12 text-center text-tertiary text-sm px-4">
                                {t("tradeComms.noInbox", { defaultValue: "Nessuna email in inbox" })}
                            </div>
                        )}
                        {isLimbo && filteredLimbo.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-12 text-center text-tertiary text-sm px-4">
                                {t("tradeComms.noLimbo", { defaultValue: "Nessuna email in limbo" })}
                            </div>
                        )}
                    </div>
                </div>
                {isDetailOpen && (
                    <div className="flex-1 min-w-0 flex flex-col px-2 py-1 overflow-hidden">
                        <EmailDetailPanel
                            messageId={selectedMessageId}
                            attentionLabels={selectedAttentionLabels}
                            emptyMessage={t("tradeComms.selectEmail", { defaultValue: "Seleziona un'email per visualizzare il contenuto" })}
                            onCloseDetail={() => openDetail(null)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
